'use client';

/**
 * ArchTabsCard — single canvas slot that toggles between the architecture
 * tree (ArchCapabilityMapCard) and the flow viz (ArchTopologyCard).
 *
 * Replaces the two-up side-by-side layout the cards used to occupy in
 * mode7/reception. The two viz components are heavy (reagraph + Pixi)
 * and each wants a generous canvas; competing for half the viewport
 * felt cramped, full-width-stacked felt distorted. Tabs let each viz
 * own the full slot when active. Selection (`useGraphSelectionStore`)
 * is shared, so clicks on either tab still populate the details panel
 * underneath.
 *
 * Sizing is owned here, not at the placement: `h-[70vh]` so the slot
 * scales with the viewport. When the placement-level height refactor
 * lands (`height_mode` on ContainerPlacement) this constant moves to
 * `web_reception.py`.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useState } from 'react';
import { Network, GitFork } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { ArchCapabilityMapCard } from './arch-capability-map-card';
import { ArchTopologyCard } from './arch-topology-card';


type ArchTab = 'tree' | 'flow';

const TABS: ReadonlyArray<{ key: ArchTab; label: string; icon: typeof Network }> = [
  { key: 'tree', label: 'Tree', icon: Network },
  { key: 'flow', label: 'Flow', icon: GitFork },
];


export function ArchTabsCard() {
  const [active, setActive] = useState<ArchTab>('tree');

  return (
    <div className="flex flex-col h-[70vh] w-full">
      <div className="flex items-center gap-1 border-b border-border/60 px-1 pb-1">
        {TABS.map(({ key, label, icon: Icon }) => {
          const selected = active === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setActive(key)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors',
                selected
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
              )}
              aria-pressed={selected}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          );
        })}
      </div>
      <div className="flex-1 min-h-0 relative">
        {active === 'tree' ? <ArchCapabilityMapCard /> : <ArchTopologyCard />}
      </div>
    </div>
  );
}
